import { useEffect, useRef } from "react";

/**
 * Loads the Google reCAPTCHA script lazily — only once the form is about to enter
 * the viewport (or the user interacts with it), instead of on initial page load.
 *
 * reCAPTCHA's api.js is heavy and sets a third-party cookie; loading it eagerly on
 * every page added significant Total Blocking Time. The form's captcha row already
 * reserves its height, so deferring the widget does not introduce layout shift.
 *
 * Returns a ref to attach to the form container. `onLoaded` runs once the script has
 * loaded (wire up the global callbacks there); `onCleanup` runs on unmount.
 */
export function useDeferredRecaptcha(
  onLoaded: () => void,
  onCleanup?: () => void,
) {
  const ref = useRef<HTMLDivElement>(null);
  const onLoadedRef = useRef(onLoaded);
  const onCleanupRef = useRef(onCleanup);
  onLoadedRef.current = onLoaded;
  onCleanupRef.current = onCleanup;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let script: HTMLScriptElement | null = null;
    let loaded = false;

    const detach = () => {
      io.disconnect();
      el.removeEventListener("focusin", load);
      el.removeEventListener("pointerdown", load);
    };

    function load() {
      if (loaded) return;
      loaded = true;
      detach();

      script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => onLoadedRef.current();
      document.body.appendChild(script);
    }

    // Preload a little before the form scrolls into view so the widget is ready.
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) load();
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    el.addEventListener("focusin", load);
    el.addEventListener("pointerdown", load);

    return () => {
      detach();
      if (script) {
        try {
          document.body.removeChild(script);
        } catch {
          /* already removed */
        }
      }
      onCleanupRef.current?.();
    };
  }, []);

  return ref;
}
